import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check } from 'lucide-react'

export default function DigitalPartnershipPage() {
  const breadcrumbs = generateBreadcrumbs('/services/digital-partnership', breadcrumbLabels)

  useSEO({
    title: 'Ongoing Support | Year-Round Digital Partnership',
    description:
      'Year-round digital support for music organizations. Website updates, email campaigns, analytics, and strategy aligned with your season.',
    url: '/services/digital-partnership',
    canonical: '/services/digital-partnership',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Year-Round Digital Partnership',
      description:
        'Ongoing digital support for classical music organizations including website updates, email campaigns, and strategic consulting.',
      url: '/services/digital-partnership',
    }),
  })

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const tiersRef = useStaggeredGrid<HTMLDivElement>('.tier-card', { stagger: 0.12 })
  const includedRef = useScrollReveal<HTMLDivElement>('.included-item', { stagger: 0.08 })
  const timelineRef = useScrollReveal<HTMLDivElement>('.timeline-item', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.08 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Ongoing Support for Music Organizations</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your organization doesn&apos;t stop after launching a new website. You need email campaigns during concert
          season. Website updates throughout the year. Analytics to track ticket sales. SEO improvements for next
          season.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          Our Year-Round Digital Partnership gives you ongoing support tailored to your organization&apos;s
          rhythm—whether you&apos;re running a festival, orchestra, opera company, or choral organization. Focus on
          creating transformative musical experiences while we handle your digital strategy.
        </p>
        <p className="text-fluid-body text-foreground mx-auto max-w-2xl font-medium">
          Think of us as an extension of your team—someone who understands both the music and the tech.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="/book" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Music Organizations Need Year-Round Digital Support</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              problem: 'Seasonal Cycles & Evolving Needs',
              description:
                'Your digital needs change throughout the year. Concert season requires active email marketing. Summer needs website updates and season planning. You need someone who understands this rhythm.',
            },
            {
              problem: 'Ongoing Website Updates',
              description:
                'Concert dates change. Artists get added. Programs evolve. You need someone who can update your website quickly without waiting weeks for a developer to respond.',
            },
            {
              problem: 'Email Campaigns for Audience Engagement',
              description:
                "Season announcements. Ticket launch emails. Concert reminders. Donor campaigns. These don't write and send themselves—you need consistent campaign management.",
            },
            {
              problem: 'No Time for SEO or Analytics',
              description:
                'You know you should be tracking website traffic, ticket sales sources, and audience metrics. But who has time? You need someone monitoring this continuously.',
            },
            {
              problem: 'Technical Issues & Bug Fixes',
              description:
                "Forms break. Integrations stop working. Emails bounce. When tech issues happen during ticket launch week, you can't wait days for a fix. You need immediate support.",
            },
            {
              problem: 'No Strategic Partner',
              description:
                "You need someone who understands your organization's goals, tracks progress year over year, and proactively suggests improvements. Not just a vendor who builds once and disappears.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-card border-border space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-4 text-center italic">
          A one-time website build isn&apos;t enough. Organizations need ongoing digital support aligned with their
          season and operational cycle.
        </p>
      </section>

      {/* Partnership Tiers Section */}
      <section className="mx-auto max-w-5xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">Three Partnership Tiers</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Choose the level of support that matches your organization&apos;s growth stage and digital needs.
          </p>
        </div>

        <div ref={tiersRef} className="grid gap-6 md:grid-cols-3">
          {/* Essential Support */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">Essential Plan</h3>
              <p className="text-primary mt-1 text-lg font-bold">$149-$199/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                For individuals and small organizations with stable websites.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Website hosting & maintenance',
                'Monthly content updates (concerts, programs, artist bios)',
                'Technical support & bug fixes',
                'Basic analytics reporting',
                'Email support (48-hour response)',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Growth Partner */}
          <motion.div
            className="tier-card border-primary bg-primary/5 space-y-4 rounded-lg border-2 p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div className="text-primary text-xs font-semibold tracking-wider uppercase">Most Popular</div>
            <div>
              <h3 className="text-xl font-semibold text-white">Growth Plan</h3>
              <p className="text-primary mt-1 text-lg font-bold">$299-$499/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                For organizations focused on growing audiences and digital presence.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Essential Support',
                'Monthly email campaigns (season announcements, ticket launches)',
                'Audience engagement optimization',
                'SEO monitoring & improvements',
                'Quarterly strategy calls',
                'Priority support (24-hour response)',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Full-Service Digital */}
          <motion.div
            className="tier-card border-border bg-background/50 space-y-4 rounded-lg border p-6"
            whileHover={{ y: -4 }}
            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          >
            <div>
              <h3 className="text-xl font-semibold text-white">Partnership Plan</h3>
              <p className="text-primary mt-1 text-lg font-bold">$999-$1,500/month</p>
              <p className="text-muted-foreground mt-2 text-sm">
                For organizations that want a comprehensive digital growth partner.
              </p>
            </div>
            <ul className="space-y-2 text-sm">
              {[
                'Everything in Growth Partner',
                'CRM management & optimization',
                'Advanced analytics & audience tracking',
                'Social media content strategy',
                'Paid ad campaign management (ad spend separate)',
                'Weekly check-ins & on-call support',
              ].map((feature, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span className="text-muted-foreground">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* What's Included Across All Tiers */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">What&apos;s Included Across All Tiers</h2>
        <p className="text-muted-foreground mx-auto max-w-2xl text-center">
          Core services that every partnership includes, regardless of tier.
        </p>

        <div ref={includedRef} className="space-y-6">
          {[
            {
              title: 'Season-Aligned Planning',
              description:
                "We align digital strategy with your organization's calendar—from season planning to concert launches to summer programming.",
            },
            {
              title: 'Ongoing Website Updates',
              description:
                'Concert announcements, artist bios, program changes, schedule adjustments, and content refreshes handled promptly.',
            },
            {
              title: 'Technical Maintenance',
              description:
                'Hosting, security updates, backups, SSL management, and performance monitoring—all managed for you.',
            },
            {
              title: 'Analytics & Reporting',
              description:
                "Track website traffic, ticket sales funnels, and audience engagement data to understand what's working.",
            },
            {
              title: 'Strategic Guidance',
              description:
                "Regular consultation on digital strategy, audience growth, and tech improvements tailored to your organization's goals.",
            },
            {
              title: 'Direct Access',
              description:
                'No ticketing systems or endless back-and-forth. Email, call, or Slack directly with someone who knows your organization.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="included-item border-primary bg-background/50 rounded-lg border p-5 backdrop-blur-sm"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold text-white">{item.title}</h3>
              <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How It Works - Example for Concert Organizations */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Example: How a Partnership Works for a Concert Organization</h2>
        <div ref={timelineRef} className="space-y-6">
          {[
            {
              month: 'January-March: Planning Season',
              description:
                "We help plan your next season's digital strategy, update website content, and prepare email campaigns for season ticket sales launch.",
            },
            {
              month: 'April-May: Season Launch',
              description:
                'Email campaigns to announce new season and drive early ticket sales, SEO improvements to rank for concert searches, website updates with new repertoire and artists.',
            },
            {
              month: 'June-August: Summer Season',
              description:
                'Ongoing concert promotion emails, ticket sales tracking and optimization, quick updates for programming changes, technical support for any issues.',
            },
            {
              month: 'September-November: Fall Season',
              description:
                'Continue audience engagement campaigns, optimize donation appeals, update website with new programs and artist info.',
            },
            {
              month: 'December: Year-End & Strategic Planning',
              description:
                'Holiday concert promotions, year-end giving campaigns, review annual data, set digital goals for next season.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="timeline-item border-primary space-y-2 border-l-4 py-3 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.month}</h3>
              <p className="text-muted-foreground">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground pt-4 text-center italic">
          Your partnership adapts to your organization&apos;s seasonal rhythm—not a generic monthly checklist.
        </p>
      </section>

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Organizations Choose Partnership Support</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              benefit: 'Predictable Monthly Investment',
              description: 'No surprise invoices. Budgetable monthly cost. Pause anytime if your needs change.',
            },
            {
              benefit: 'Continuous Improvement',
              description:
                'We track metrics year over year and continuously optimize your website, email campaigns, and audience engagement strategies.',
            },
            {
              benefit: 'Faster Response Times',
              description:
                'Partnership clients get priority support. Bug fixes within 24 hours. Content updates within 48 hours. No waiting weeks for availability.',
            },
            {
              benefit: 'Proactive Strategy',
              description:
                "We don't wait for you to ask. We proactively suggest improvements, identify opportunities, and keep you ahead of the curve.",
            },
            {
              benefit: 'Deep Organizational Knowledge',
              description:
                'After a few months, we know your programs, your audience, your goals. We become true partners, not just vendors.',
            },
            {
              benefit: 'Audience Growth Over Time',
              description:
                'Small optimizations compound. Email campaigns improve. SEO rankings increase. Ticket conversion rates rise. Audiences grow year over year.',
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="benefit-card border-primary/20 bg-primary/5 space-y-2 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.benefit}</h3>
              <p className="text-muted-foreground text-sm">{item.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'Can we start with a lower tier and upgrade later?',
              answer:
                'Absolutely. Many organizations start with Essential Support and upgrade to Growth Partner as their needs grow. You can adjust your tier anytime.',
            },
            {
              question: 'What happens if we need to pause the partnership?',
              answer:
                "No problem. You can pause your partnership anytime (for example, during summer if you don't have active programming). Just give us 30 days notice.",
            },
            {
              question: 'Do we need to have our website built by you first?',
              answer:
                'Not necessarily. We can provide partnership support for websites built by other developers. However, we work most efficiently with sites we built because we know the codebase.',
            },
            {
              question: 'How do email campaigns work?',
              answer:
                "We write, design, and schedule email campaigns aligned with your organization's calendar. You review and approve before sending. We track open rates, click rates, and adjust strategy based on data.",
            },
            {
              question: 'Can we request additional features or projects during the partnership?',
              answer:
                'Yes. Small features are included in your monthly hours. Larger projects (new ticketing systems, major redesigns) can be quoted separately.',
            },
            {
              question: 'How is this different from hiring a full-time marketing person?',
              answer:
                "You get specialized classical music + digital marketing expertise for a fraction of the cost of a full-time hire. Plus, we bring technical skills (development, integrations, analytics) that most marketing staff don't have.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Grow Your Digital Presence?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute strategy call to discuss your organization&apos;s needs and find the right
            partnership tier for your goals.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="/book" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Schedule Your Strategy Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No sales pitch. Just an honest conversation about how ongoing support can help your organization grow.
          </p>
        </div>
      </section>
    </div>
  )
}
